import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiAlertTriangle, FiCheckCircle, FiCornerUpRight, FiExternalLink, FiLink2, FiTool } from "react-icons/fi";
import { Modal } from "./Modal";
import { LoadingSpinner } from "./LoadingSpinner";
import GatedButton from "./GatedButton";
import ContentPickerPanel, { PickedContent } from "./Redirects/ContentPickerPanel";
import {
	RemovalImpact,
	createRedirect,
	displayPath,
	editorRouteFor,
	extractErrorMessage,
	fetchImpact,
	fixContentSource
} from "./Redirects/redirectsApi";

type ResolutionMode = "fix" | "redirect" | null;

type RemovalImpactModalProps = {
	show: boolean;
	/** Le contenu qu'on s'apprête à retirer. */
	contentId: string;
	/** L'impact évalué (GET /api/redirects/impact) : la modale ne s'ouvre que si de VRAIS
	 * liens statiques entrants existent (une redirection visant l'URL ne suffit plus). */
	impact: RemovalImpact | null;
	/** Libellé de l'action finale (« Dépublier » par défaut) : la modale est agnostique du flux. */
	actionLabel?: string;
	/** True pendant que l'action finale (dépublication/suppression) tourne. */
	busy?: boolean;
	/** Erreur de l'action finale, affichée dans la modale. */
	error?: string | null;
	/** Poursuit l'action (après correction, ou « quand même »). */
	onProceed: () => void;
	onCancel: () => void;
};

/**
 * Modale d'impact AVANT retrait : « Cette page est encore référencée ». Liste les liens statiques
 * entrants et les redirections qui visent l'URL, puis propose trois issues : corriger les liens
 * maintenant (sélecteur de contenu → fix-source), créer une redirection (fromPath verrouillé),
 * ou retirer quand même (l'erreur restera visible dans Redirections). Agnostique du contenu :
 * réutilisable pour la dépublication comme pour la suppression.
 */
const RemovalImpactModal: React.FC<RemovalImpactModalProps> = ({
	show,
	contentId,
	impact,
	actionLabel = "Dépublier",
	busy,
	error,
	onProceed,
	onCancel
}) => {
	// L'impact affiché : rafraîchi après chaque correction pour que les listes suivent la réalité.
	const [currentImpact, setCurrentImpact] = useState<RemovalImpact | null>(impact);
	const [mode, setMode] = useState<ResolutionMode>(null);
	const [resolved, setResolved] = useState(false);
	const [resolutionNote, setResolutionNote] = useState<string | null>(null);

	// « Corriger les liens maintenant »
	const [selected, setSelected] = useState<PickedContent | null>(null);
	const [fixing, setFixing] = useState(false);
	const [fixError, setFixError] = useState<string | null>(null);

	// « Créer une redirection »
	const [toPath, setToPath] = useState("");
	const [isPermanent, setIsPermanent] = useState(true);
	const [creating, setCreating] = useState(false);
	const [createError, setCreateError] = useState<string | null>(null);

	useEffect(() => {
		if (show) {
			setCurrentImpact(impact);
			setMode(null);
			setResolved(false);
			setResolutionNote(null);
			setSelected(null);
			setFixing(false);
			setFixError(null);
			setToPath("");
			setIsPermanent(true);
			setCreating(false);
			setCreateError(null);
		}
	}, [show, impact]);

	const refreshImpact = async () => {
		try {
			setCurrentImpact(await fetchImpact(contentId));
		} catch {
			/* on garde les listes connues : l'affichage reste utilisable */
		}
	};

	const runFix = async () => {
		if (!selected?.id) return;
		setFixing(true);
		setFixError(null);
		try {
			const result = await fixContentSource(contentId, selected.id);
			const done = result.remainingStaticLinks === 0 && result.remainingTargetingRedirects === 0;
			setResolutionNote(
				`${result.rewrittenContentIds.length} lien${result.rewrittenContentIds.length > 1 ? "s" : ""} réécrit${
					result.rewrittenContentIds.length > 1 ? "s" : ""
				}, ${result.repointedRedirects.length} redirection${result.repointedRedirects.length > 1 ? "s" : ""} repointée${
					result.repointedRedirects.length > 1 ? "s" : ""
				}.` +
					(result.refusedRedirects.length
						? ` ${result.refusedRedirects.length} redirection${result.refusedRedirects.length > 1 ? "s" : ""} refusée${
								result.refusedRedirects.length > 1 ? "s" : ""
						  } : ${result.refusedRedirects.map((refused) => refused.message).filter(Boolean).join(" · ")}`
						: "")
			);
			if (done) {
				setResolved(true);
				setMode(null);
			} else {
				setFixError(
					`Il reste ${result.remainingStaticLinks} lien(s) et ${result.remainingTargetingRedirects} redirection(s) à traiter.`
				);
			}
			await refreshImpact();
		} catch (err) {
			console.error("Échec de la correction à la source", err);
			setFixError(extractErrorMessage(err) ?? "La correction a échoué. Réessayez.");
		} finally {
			setFixing(false);
		}
	};

	const runCreateRedirect = async () => {
		const from = (currentImpact?.url ?? "").trim();
		const to = toPath.trim();
		if (!from || !to) {
			setCreateError("Renseignez la destination de la redirection.");
			return;
		}
		setCreating(true);
		setCreateError(null);
		try {
			await createRedirect(from, to, isPermanent);
			toast.success("Redirection créée.");
			setResolutionNote(`Redirection créée : ${displayPath(from)} → ${displayPath(to)}.`);
			setResolved(true);
			setMode(null);
		} catch (err) {
			console.error("Échec de la création de la redirection", err);
			setCreateError(extractErrorMessage(err) ?? "Impossible de créer la redirection.");
		} finally {
			setCreating(false);
		}
	};

	const close = () => {
		if (!busy && !fixing && !creating) onCancel();
	};

	const staticLinks = currentImpact?.staticLinks ?? [];
	const targetingRedirects = currentImpact?.targetingRedirects ?? [];

	const choiceButtonClass = (active: boolean) =>
		`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
			active
				? "border-slate-900 bg-slate-900 text-white shadow-sm"
				: "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
		}`;

	return (
		<Modal show={show} onHide={close} size="md" centered>
			<Modal.Header closeButton>
				<Modal.Title>Cette page est encore référencée</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="space-y-4">
					<div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
						<FiAlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
						<p className="text-sm text-slate-700">
							Retirer{" "}
							<span className="rounded bg-white px-1.5 py-0.5 font-mono text-xs text-slate-700" title={displayPath(currentImpact?.url)}>
								{displayPath(currentImpact?.url)}
							</span>{" "}
							casserait les références ci-dessous. Corrigez-les maintenant, ou poursuivez : le chemin
							cassé (404) est assumé. Une redirection reste possible si elle est vraiment nécessaire.
						</p>
					</div>

					{staticLinks.length > 0 && (
						<div>
							<p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
								<FiLink2 className="h-3.5 w-3.5" /> Liens statiques entrants ({staticLinks.length})
							</p>
							<ul className="mt-1.5 space-y-1">
								{staticLinks.map((link, index) => {
									const route = editorRouteFor(link.schemaKey, link.contentId);
									const label = link.title?.trim() || link.contentId || "Contenu inconnu";
									return (
										<li
											key={`impact-link-${link.contentId ?? index}-${index}`}
											className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
										>
											{route ? (
												<a
													href={route}
													target="_blank"
													rel="noopener noreferrer"
													className="min-w-0 truncate font-semibold text-slate-800 transition hover:text-sky-700 hover:underline"
													title={`Ouvrir « ${label} » dans l'éditeur (nouvel onglet)`}
												>
													{label}
												</a>
											) : (
												<span className="min-w-0 truncate font-semibold text-slate-800" title={label}>
													{label}
												</span>
											)}
											{link.linkLabel && (
												<span className="min-w-0 truncate text-xs text-slate-500" title={link.linkLabel}>
													« {link.linkLabel} »
												</span>
											)}
											{route && <FiExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-400" />}
										</li>
									);
								})}
							</ul>
						</div>
					)}

					{targetingRedirects.length > 0 && (
						<div>
							<p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
								<FiCornerUpRight className="h-3.5 w-3.5" /> Redirections visant cette page ({targetingRedirects.length})
							</p>
							<ul className="mt-1.5 space-y-1">
								{targetingRedirects.map((rule, index) => (
									<li
										key={`impact-rule-${rule.id ?? index}`}
										className="truncate rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-700"
										title={`${displayPath(rule.fromPath)} → ${displayPath(rule.toPath)}`}
									>
										{displayPath(rule.fromPath)} <span className="text-slate-400">→</span> {displayPath(rule.toPath)}
									</li>
								))}
							</ul>
						</div>
					)}

					{resolved ? (
						<div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
							<FiCheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
							<div className="text-sm text-slate-700">
								<p className="font-semibold text-slate-900">Références traitées.</p>
								{resolutionNote && <p className="mt-0.5">{resolutionNote}</p>}
								<p className="mt-0.5">Vous pouvez maintenant poursuivre en toute sécurité.</p>
							</div>
						</div>
					) : (
						<div className="space-y-3">
							{/* Corriger / rediriger écrivent côté serveur (403 sans feature:redirections:write) : « Dépublier quand même » et « Annuler » restent ouverts à tous. */}
							<div className="flex flex-wrap gap-2">
								<GatedButton
									token="feature:redirections:write"
									onClick={() => setMode(mode === "fix" ? null : "fix")}
									className={choiceButtonClass(mode === "fix")}
								>
									<FiTool className="h-4 w-4" /> Corriger les liens maintenant
								</GatedButton>
								<GatedButton
									token="feature:redirections:write"
									onClick={() => setMode(mode === "redirect" ? null : "redirect")}
									className={choiceButtonClass(mode === "redirect")}
								>
									<FiCornerUpRight className="h-4 w-4" /> Créer une redirection
								</GatedButton>
							</div>

							{mode === "fix" && (
								<div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
									<p className="text-sm text-slate-600">
										Choisissez la page de remplacement : les liens seront réécrits et les redirections repointées.
									</p>
									<ContentPickerPanel
										excludeIds={[contentId]}
										selectedId={selected?.id ?? null}
										onSelect={setSelected}
										listMaxHeightClass="max-h-48"
									/>
									{fixError && (
										<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
											{fixError}
										</p>
									)}
									{resolutionNote && !resolved && (
										<p className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">{resolutionNote}</p>
									)}
									<div className="flex justify-end">
										<button
											type="button"
											onClick={runFix}
											disabled={!selected || fixing}
											className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
										>
											{fixing ? <LoadingSpinner size="sm" inline /> : <FiTool className="h-4 w-4" />}
											{fixing ? "Correction…" : "Réécrire les liens"}
										</button>
									</div>
								</div>
							)}

							{mode === "redirect" && (
								<div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
									<p className="text-sm text-slate-600">
										Les visiteurs de l'ancienne URL seront redirigés : les liens sources restent inchangés.
									</p>
									<div className="space-y-2">
										<label className="block">
											<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Depuis (verrouillé)</span>
											<input
												type="text"
												value={displayPath(currentImpact?.url)}
												readOnly
												disabled
												className="mt-1 w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 font-mono text-xs text-slate-500"
											/>
										</label>
										<label className="block">
											<span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Vers</span>
											<input
												type="text"
												value={toPath}
												onChange={(e) => setToPath(e.target.value)}
												placeholder="/nouvelle-url"
												className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-700 placeholder:text-slate-400 transition focus:border-slate-400 focus:outline-none"
											/>
										</label>
										<label className="flex items-center gap-2 text-sm text-slate-700">
											<input
												type="checkbox"
												checked={isPermanent}
												onChange={(e) => setIsPermanent(e.target.checked)}
												className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
											/>
											Permanente (301)
										</label>
									</div>
									{createError && (
										<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
											{createError}
										</p>
									)}
									<div className="flex justify-end">
										<button
											type="button"
											onClick={runCreateRedirect}
											disabled={creating || !toPath.trim()}
											className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
										>
											{creating ? <LoadingSpinner size="sm" inline /> : <FiCornerUpRight className="h-4 w-4" />}
											{creating ? "Création…" : "Créer la redirection"}
										</button>
									</div>
								</div>
							)}
						</div>
					)}

					{error && (
						<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
							{error}
						</p>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer className="flex-wrap">
				{!resolved && (
					<p className="w-full text-xs text-slate-500">
						« {actionLabel} quand même » : le 404 est assumé ; la tâche restera visible dans Redirections
						tant que des liens statiques pointeront vers cette URL.
					</p>
				)}
				<button
					type="button"
					onClick={close}
					disabled={busy}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
				>
					Annuler
				</button>
				{!resolved && (
					<button
						type="button"
						onClick={onProceed}
						disabled={busy}
						title="L'erreur restera visible dans Redirections jusqu'à correction."
						className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 shadow-sm transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{busy ? <LoadingSpinner size="sm" inline /> : <FiAlertTriangle className="h-4 w-4" />}
						{actionLabel} quand même
					</button>
				)}
				<button
					type="button"
					onClick={onProceed}
					disabled={!resolved || busy}
					title={resolved ? undefined : "Corrigez les liens ou créez une redirection pour poursuivre sereinement."}
					className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{busy ? <LoadingSpinner size="sm" inline /> : <FiCheckCircle className="h-4 w-4" />}
					{actionLabel}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default RemovalImpactModal;
